<script setup lang="ts">
import { onMounted, ref, type HTMLAttributes } from 'vue'
import { cn } from '@scripts/utils.js'
import type { EmblaCarouselType } from 'embla-carousel'
import Autoplay, { type AutoplayOptionsType } from 'embla-carousel-autoplay'
import type useEmblaCarousel from 'embla-carousel-vue'
import emblaCarouselVue from 'embla-carousel-vue'
import Progress from '@components/Progress.vue'

type UseCarouselParameters = Parameters<typeof useEmblaCarousel>
type CarouselOptions = UseCarouselParameters[0]

const {
    options = {
        slidesToScroll: 1,
        loop: true,
    },
    autoplayOptions = {
        stopOnMouseEnter: true,
        stopOnInteraction: false,
        delay: 4000,
    },
    paginationClass = '',
} = defineProps<{
    options?: CarouselOptions
    autoplayOptions?: AutoplayOptionsType
    paginationClass?: HTMLAttributes['class']
}>()

const [emblaRef, emblaApi] = emblaCarouselVue(options, [
    Autoplay(autoplayOptions),
])

const paused = ref(false)
const resumedAt = ref<number | null>(null)
const selectedIndex = ref<number | null>(null)
const scrollSnaps = ref<number[]>([])

function onSelect(api: EmblaCarouselType) {
    selectedIndex.value = api.selectedScrollSnap()
    scrollSnaps.value = api.scrollSnapList()
}

onMounted(() => {
    if (!emblaApi.value) return

    emblaApi.value
        .on('init', onSelect)
        .on('reInit', onSelect)
        .on('select', onSelect)
        .on('autoplay:play', () => {
            paused.value = false
        })
        .on('autoplay:stop', () => {
            paused.value = true
        })
        .on('autoplay:timerset', () => {
            resumedAt.value = new Date().getTime()
        })
})
</script>

<template>
    <div
        class="overflow-hidden rounded"
        ref="emblaRef"
    >
        <div class="flex touch-pan-x">
            <slot />
        </div>
        <ul :class="cn('flex gap-x-2', paginationClass)">
            <li
                v-for="(_, index) in scrollSnaps"
                class="min-w-0 flex-1"
            >
                <button
                    class="flex h-10 w-full cursor-pointer items-center *:flex-1"
                    @click="emblaApi?.scrollTo(index)"
                >
                    <Progress
                        :key="`slide-${index}-${resumedAt}`"
                        variant="transparent"
                        :animate="
                            selectedIndex === index
                                ? {
                                      duration: autoplayOptions.delay as number,
                                      paused: paused,
                                  }
                                : false
                        "
                    />
                    <span class="sr-only">Seite {{ index }}</span>
                </button>
            </li>
        </ul>
    </div>
</template>
